import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2 } from 'lucide-react';
import { useGetFeedSportsQuery, useGetFeedLeaguesQuery } from '@/api/feed';

interface SportLeagueStepProps {
  selectedSportId?: string;
  selectedLeagueId?: string;
  restrictedLeagueId?: string;
  onSelectSport: (sportId: string, sportCode: string, sportName: string) => void;
  onSelectLeague: (leagueId: string, leagueName: string) => void;
}

export function SportLeagueStep({
  selectedSportId,
  selectedLeagueId,
  restrictedLeagueId,
  onSelectSport,
  onSelectLeague,
}: SportLeagueStepProps) {
  const { data: sports, isLoading: sportsLoading, error: sportsError } = useGetFeedSportsQuery();

  const {
    data: leagues,
    isLoading: leaguesLoading,
    error: leaguesError,
  } = useGetFeedLeaguesQuery(selectedSportId);

  if (sportsLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading sports...</span>
      </div>
    );
  }

  if (sportsError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load sports. Please try again.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="sport-select">Sport</Label>
        <Select
          value={selectedSportId || ''}
          disabled={!!restrictedLeagueId}
          onValueChange={value => {
            const sport = sports?.find(s => s.externalId === value);
            if (sport) {
              onSelectSport(sport.externalId, sport.code, sport.name);
            }
          }}
        >
          <SelectTrigger id="sport-select" data-testid="sport-select">
            <SelectValue placeholder="Select a sport" />
          </SelectTrigger>
          <SelectContent>
            {sports?.map(sport => (
              <SelectItem
                key={sport.externalId}
                value={sport.externalId}
                data-testid={`sport-option-${sport.externalId}`}
              >
                {sport.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedSportId && (
        <div className="space-y-2">
          <Label htmlFor="league-select">League</Label>
          {leaguesLoading ? (
            <div className="flex items-center gap-2 py-2">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Loading leagues...</span>
            </div>
          ) : leaguesError ? (
            <Alert variant="destructive">
              <AlertDescription>Failed to load leagues. Please try again.</AlertDescription>
            </Alert>
          ) : !leagues || leagues.length === 0 ? (
            <Alert>
              <AlertDescription>No leagues found for this sport.</AlertDescription>
            </Alert>
          ) : (
            <Select
              value={selectedLeagueId || ''}
              disabled={!!restrictedLeagueId}
              onValueChange={value => {
                const league = leagues.find(l => l.externalId === value);
                if (league) {
                  onSelectLeague(league.externalId, league.name);
                }
              }}
            >
              <SelectTrigger id="league-select" data-testid="league-select">
                <SelectValue placeholder="Select a league" />
              </SelectTrigger>
              <SelectContent>
                {leagues.map(league => (
                  <SelectItem
                    key={league.externalId}
                    value={league.externalId}
                    data-testid={`league-option-${league.externalId}`}
                  >
                    {league.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}
    </div>
  );
}
